import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API, asset } from "../../lib/api";
import { Input } from "../ui/input";
import { ChevronUp, ChevronDown, Eye, EyeOff, RefreshCw } from "lucide-react";

const GROUPS = [["showcase", "Major events showcase"], ["crash", "Daytona crash film strip (left to right)"], ["origin", "Where it started"]];

function Row({ it, hdr, onSaved, onMove, idx, count }) {
  const [caption, setCaption] = useState(it.caption || "");
  const [year, setYear] = useState(it.year || "");
  const patch = async (body, msg) => {
    try { await axios.patch(`${API}/admin/legacy/${it.id}`, body, hdr()); if (msg) toast.success(msg); onSaved(); }
    catch { toast.error("Save failed"); }
  };
  const dirty = caption !== (it.caption || "") || year !== (it.year || "");
  return (
    <div data-testid={`legacy-row-${it.id}`} className={`flex items-center gap-3 border border-white/10 rounded-lg p-2 bg-obsidian-800 ${it.hidden ? "opacity-50" : ""}`}>
      <span className="font-mono text-xs text-neutral-500 w-6 text-right">{idx + 1}</span>
      <img src={asset(it.url)} alt="" className="w-20 h-14 object-contain bg-black rounded" />
      <Input data-testid={`legacy-caption-${it.id}`} value={caption} onChange={(e) => setCaption(e.target.value)} className="flex-1 text-sm" />
      <Input data-testid={`legacy-year-${it.id}`} value={year} onChange={(e) => setYear(e.target.value)} placeholder="Year" className="w-24 text-sm" />
      <button data-testid={`legacy-save-${it.id}`} disabled={!dirty} onClick={() => patch({ caption, year }, "Saved")}
        className="btn-amber rounded-full px-3 py-1.5 text-xs disabled:opacity-30">Save</button>
      <button data-testid={`legacy-hide-${it.id}`} title={it.hidden ? "Show on site" : "Hide from site"} onClick={() => patch({ hidden: !it.hidden })}
        className="text-neutral-400 hover:text-white">{it.hidden ? <EyeOff size={16} /> : <Eye size={16} />}</button>
      <div className="flex flex-col">
        <button data-testid={`legacy-up-${it.id}`} disabled={idx === 0} onClick={() => onMove(idx, idx - 1)} className="disabled:opacity-20"><ChevronUp size={15} /></button>
        <button data-testid={`legacy-down-${it.id}`} disabled={idx === count - 1} onClick={() => onMove(idx, idx + 1)} className="disabled:opacity-20"><ChevronDown size={15} /></button>
      </div>
    </div>
  );
}

export function LegacyAdmin({ hdr }) {
  const [items, setItems] = useState([]);
  const load = useCallback(() => axios.get(`${API}/admin/legacy`, hdr()).then(({ data }) => setItems(data)).catch(() => {}), [hdr]);
  useEffect(() => { load(); }, [load]);
  const move = (list) => async (from, to) => {
    const ids = list.map((x) => x.id);
    const [x] = ids.splice(from, 1);
    ids.splice(to, 0, x);
    await axios.post(`${API}/admin/legacy/reorder`, { ids }, hdr());
    load();
  };
  return (
    <div data-testid="admin-legacy" className="space-y-12">
      <div className="flex items-center justify-between">
        <p className="text-neutral-400 text-sm max-w-2xl">Your 1992–now major events, shown on the homepage and the Incognito page. Fix any caption or year, hide a photo, or use the arrows to change the order.</p>
        <button onClick={load} className="text-sm text-neutral-400 flex items-center gap-1 hover:text-white"><RefreshCw size={13} /> Refresh</button>
      </div>
      {GROUPS.map(([g, label]) => {
        const list = items.filter((i) => i.group === g);
        return (
          <div key={g}>
            <h3 className="font-serif text-2xl font-light mb-4">{label}</h3>
            <div className="space-y-2">
              {list.map((it, i) => <Row key={`${it.id}-${it.caption}-${it.year}`} it={it} hdr={hdr} onSaved={load} onMove={move(list)} idx={i} count={list.length} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
